/**
 * White Label Branding Types
 *
 * Defines the structure for organization branding settings.
 * These settings control how the app appears for each organization's
 * trainers and clients.
 */

/**
 * HSL color value as a string (e.g., "150 50% 25%")
 * Does NOT include the `hsl()` wrapper - just the raw values
 */
export type HSLColor = string;

/**
 * Color palette for an organization's theme
 * Maps to CSS variables in index.css
 */
export interface BrandingColors {
  /** Primary brand color - used for buttons, links, accents */
  primary: HSLColor;
  /** Primary text color on primary backgrounds */
  primaryForeground?: HSLColor;
  /** Secondary/muted background color */
  secondary?: HSLColor;
  /** Secondary text color */
  secondaryForeground?: HSLColor;
  /** Accent color for highlights */
  accent?: HSLColor;
  /** Accent text color */
  accentForeground?: HSLColor;
  /** Background color */
  background?: HSLColor;
  /** Main text color */
  foreground?: HSLColor;
  /** Muted backgrounds */
  muted?: HSLColor;
  /** Muted text color */
  mutedForeground?: HSLColor;
  /** Border color */
  border?: HSLColor;
  /** Ring/focus color */
  ring?: HSLColor;
  /** Info color - used for informational states */
  info?: HSLColor;
  /** Info text color */
  infoForeground?: HSLColor;
  /** Success color - used for positive states */
  success?: HSLColor;
  /** Success text color */
  successForeground?: HSLColor;
  /** Warning color - used for caution states */
  warning?: HSLColor;
  /** Warning text color */
  warningForeground?: HSLColor;
  /** Chart color 1 */
  chart1?: HSLColor;
  /** Chart color 2 */
  chart2?: HSLColor;
  /** Chart color 3 */
  chart3?: HSLColor;
  /** Chart color 4 */
  chart4?: HSLColor;
  /** Chart color 5 */
  chart5?: HSLColor;
  /** Chart color 6 */
  chart6?: HSLColor;
  /** Chart color 7 */
  chart7?: HSLColor;
  /** Chart color 8 */
  chart8?: HSLColor;
}

/**
 * Email branding configuration
 */
export interface EmailBranding {
  /** Sender name for transactional emails */
  senderName?: string;
  /** Reply-to email address */
  replyTo?: string;
  /** Support email address */
  supportEmail?: string;
  /** Custom footer text for emails */
  footerText?: string;
  /** Whether to show "powered by" footer (Enterprise can disable) */
  showPoweredBy?: boolean;
}

/**
 * External links for the organization
 */
export interface BrandingLinks {
  statusPage?: string;
  twitter?: string;
  instagram?: string;
  linkedin?: string;
}

/**
 * Legal/compliance information
 */
export interface LegalBranding {
  /** Company legal name */
  companyName?: string;
  /** Company address for legal notices */
  companyAddress?: string;
  /** Link to custom Terms of Service */
  termsUrl?: string;
  /** Link to custom Privacy Policy */
  privacyUrl?: string;
}

/**
 * Custom terminology mapping
 * Allows orgs to customize how things are labeled
 */
export interface BrandingTerminology {
  /** What to call a training session (Session, Class, Appointment) */
  session?: string;
  /** What to call a trainer (Trainer, Coach, Instructor) */
  trainer?: string;
  /** What to call a client (Client, Member, Athlete) */
  client?: string;
  /** What to call a package (Package, Plan, Bundle) */
  package?: string;
}

/**
 * Full branding configuration for an organization
 */
export interface OrganizationBranding {
  /** Display name for the business */
  business_name: string | null;
  /** Optional tagline/slogan */
  tagline?: string | null;
  /** URL to the organization's logo */
  logo_url: string | null;
  /** URL to dark mode version of logo (optional) */
  logo_dark_url?: string | null;
  /** URL to favicon */
  favicon_url?: string | null;
  /** Color palette overrides */
  colors?: BrandingColors;
  /** Email branding settings */
  email?: EmailBranding;
  /** Legal/compliance settings */
  legal?: LegalBranding;
  /** External links (social, status page, etc.) */
  links?: BrandingLinks;
  /** Custom terminology */
  terminology?: BrandingTerminology;
}

/**
 * Default branding values (TrainerPulse defaults)
 */
export const DEFAULT_BRANDING: Required<
  Pick<OrganizationBranding, "colors" | "email" | "links" | "terminology">
> & OrganizationBranding = {
  business_name: "TrainerPulse",
  tagline: null,
  logo_url: null,
  logo_dark_url: null,
  favicon_url: null,
  colors: {
    primary: "150 50% 25%",
    primaryForeground: "0 0% 100%",
    secondary: "120 10% 92%",
    secondaryForeground: "150 30% 15%",
    accent: "150 50% 35%",
    accentForeground: "0 0% 100%",
    background: "120 10% 96%",
    foreground: "150 30% 15%",
    muted: "120 10% 92%",
    mutedForeground: "150 10% 45%",
    border: "120 10% 88%",
    ring: "150 50% 25%",
    info: "210 75% 45%",
    infoForeground: "0 0% 100%",
    success: "150 50% 35%",
    successForeground: "0 0% 100%",
    warning: "38 92% 50%",
    warningForeground: "0 0% 0%",
    chart1: "150 50% 25%",
    chart2: "210 75% 45%",
    chart3: "38 92% 50%",
    chart4: "270 50% 50%",
    chart5: "0 70% 50%",
    chart6: "180 55% 35%",
    chart7: "25 90% 50%",
    chart8: "330 65% 50%",
  },
  email: {
    senderName: "TrainerPulse",
    supportEmail: "support@trainerpulse.app",
    showPoweredBy: true,
  },
  links: {
    statusPage: "https://status.trainerpulse.app",
    twitter: "https://twitter.com/trainerpulseapp",
    instagram: "https://instagram.com/trainerpulseapp",
    linkedin: "https://linkedin.com/company/trainerpulse",
  },
  terminology: {
    session: "Session",
    trainer: "Trainer",
    client: "Client",
    package: "Package",
  },
};

/**
 * CSS variable names that map to branding colors
 */
export const CSS_VARIABLE_MAP: Record<keyof BrandingColors, string> = {
  primary: "--primary",
  primaryForeground: "--primary-foreground",
  secondary: "--secondary",
  secondaryForeground: "--secondary-foreground",
  accent: "--accent",
  accentForeground: "--accent-foreground",
  background: "--background",
  foreground: "--foreground",
  muted: "--muted",
  mutedForeground: "--muted-foreground",
  border: "--border",
  ring: "--ring",
  info: "--info",
  infoForeground: "--info-foreground",
  success: "--success",
  successForeground: "--success-foreground",
  warning: "--warning",
  warningForeground: "--warning-foreground",
  chart1: "--chart-1",
  chart2: "--chart-2",
  chart3: "--chart-3",
  chart4: "--chart-4",
  chart5: "--chart-5",
  chart6: "--chart-6",
  chart7: "--chart-7",
  chart8: "--chart-8",
};

/**
 * Validates an HSL color string format
 * Expected format: "H S% L%" (e.g., "150 50% 25%")
 */
export function isValidHSLColor(value: string): boolean {
  // Pattern: number, space, number%, space, number%
  const hslPattern = /^\d{1,3}\s+\d{1,3}%\s+\d{1,3}%$/;
  return hslPattern.test(value.trim());
}

/**
 * Converts a hex color to HSL string format
 */
export function hexToHSL(hex: string): HSLColor {
  // Remove # if present
  hex = hex.replace(/^#/, "");

  // Parse hex to RGB
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  // Convert to the format we use: "H S% L%"
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Converts HSL string to hex color
 */
export function hslToHex(hsl: HSLColor): string {
  const parts = hsl.match(/(\d+)\s+(\d+)%\s+(\d+)%/);
  if (!parts) return "#000000";

  const h = parseInt(parts[1]) / 360;
  const s = parseInt(parts[2]) / 100;
  const l = parseInt(parts[3]) / 100;

  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (x: number) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
